import { IconListCheck, IconX } from '@tabler/icons-react-native';
import { useListingResults } from 'app/components/listing-results-context';
import { Row } from 'app/design/primitives/box';
import type { useScopedTranslation } from 'app/hooks/i18n';
import { Button } from 'app/shopify';
import { ListingType } from 'app/types/listing';
import type { SpecificRouteProp } from 'app/types/navigation';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import type React from 'react';
import { SelectionHeaderAction } from './selection-header-action';
import { theme } from './shared';

export function ListingsSelectionHeader({
  route,
  isEmpty,
  isSelecting,
  isSmallUI,
  t,
  selectAll,
  onSelectAll,
  onEdit,
  isLargeUI,
  selectedRowsState,
  setListingForMenuAction,
  setShowActions,
}: {
  route: SpecificRouteProp<'my-listings'>;
  isEmpty: boolean;
  isSelecting: boolean;
  isSmallUI: boolean;
  t: ReturnType<typeof useScopedTranslation>['t'];
  selectAll: boolean;
  onSelectAll: (isSelectAllPartial: boolean) => void;
  onEdit: () => void;
  isLargeUI: boolean;
  selectedRowsState: [string[], React.Dispatch<React.SetStateAction<string[]>>];
  setListingForMenuAction: React.Dispatch<React.SetStateAction<string | null>>;
  setShowActions: React.Dispatch<React.SetStateAction<boolean>>;
}): React.ReactNode {
  const { isSelectAllPartial, isSelectingAll } = useListingResults();

  if (!(route.params.type === ListingType.offer && !isEmpty)) {
    return undefined;
  }
  if (isSelecting) {
    return (
      <Row
        alignItems="center"
        gap={isSmallUI ? 'spacing.general.x2' : 'spacing.general.x4'}
        style={isSmallUI ? { justifyContent: 'space-between', width: '100%' } : undefined}
      >
        <SelectionHeaderAction
          color={theme.colors['text.primary']}
          icon={<IconListCheck color={theme.colors['icon.primary']} size={24} strokeWidth={ICON_STROKE_WIDTH} />}
          label={t(selectAll || isSelectAllPartial ? 'deselectAllButton' : 'selectAllButton')}
          onPress={() => onSelectAll(isSelectAllPartial)}
        />
        <SelectionHeaderAction
          color={theme.colors['text.primary']}
          icon={<IconX color={theme.colors['icon.primary']} size={24} strokeWidth={ICON_STROKE_WIDTH} />}
          label={t('cancelButton')}
          onPress={onEdit}
        />
        {isLargeUI ? (
          <Button
            disabled={selectedRowsState[0].length === 0 || (selectAll && isSelectingAll && !isSelectAllPartial)}
            label={t('modifyListingsCountButton', { count: selectedRowsState[0].length })}
            onPress={() => {
              setListingForMenuAction(null);
              setShowActions(true);
            }}
            size="medium"
            variant="tertiary"
          />
        ) : null}
      </Row>
    );
  }
  return (
    <SelectionHeaderAction
      color={theme.colors['text.brand']}
      icon={<IconListCheck color={theme.colors['icon.brand']} size={24} strokeWidth={ICON_STROKE_WIDTH} />}
      label={t('editButton')}
      onPress={onEdit}
    />
  );
}
